import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Plus, Search, Trash2, FileText } from "lucide-react";

export default function ETickets() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/etickets", { params: { q } });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const del = async (id) => {
    if (!window.confirm("Hapus e-ticket?")) return;
    await api.delete(`/etickets/${id}`);
    toast.success("E-ticket dihapus"); load();
  };

  const pdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/etickets/${id}/pdf?token=${localStorage.getItem("gg_token")}`;
  const originalUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/etickets/${id}/original.pdf?token=${localStorage.getItem("gg_token")}`;

  return (
    <div className="space-y-6" data-testid="etickets-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">E-Ticket</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Electronic Ticket Itinerary Gogojalan.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/etickets/upload"><Button variant="outline" data-testid="eticket-upload-btn"><Plus size={16} className="mr-1"/> Upload PDF OTA</Button></Link>
          <Link to="/etickets/new"><Button className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="new-eticket-btn"><Plus size={16} className="mr-1"/> Generate E-ticket</Button></Link>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Search size={16} className="text-slate-400"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor / passenger / PNR..." className="max-w-md" data-testid="eticket-search"/>
          </div>

          {/* Mobile: card view */}
          <div className="md:hidden space-y-3" data-testid="et-mobile">
            {list.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`et-card-${e.eticket_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mono font-bold text-slate-900 text-sm">{e.eticket_number}</div>
                    <div className="font-semibold truncate text-slate-900">{e.passenger || "-"}</div>
                    <div className="text-xs text-slate-500 truncate">{e.airline || "-"} · <span className="mono">{e.pnr || "-"}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-slate-400">Tgl</div>
                    <div className="text-xs text-slate-600">{shortDate(e.created_at)}</div>
                  </div>
                </div>
                {e.route && <div className="mt-2 text-xs text-slate-600 truncate">{e.route}</div>}
                <div className="mt-2 flex items-center justify-end gap-1 flex-wrap">
                  <a href={pdfUrl(e.id)} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" title="Gogojalan E-Ticket" className="h-8 text-xs">
                      <Download size={14} className="mr-1"/> E-Ticket
                    </Button>
                  </a>
                  {e.original_ota_filename && (
                    <a href={originalUrl(e.id)} target="_blank" rel="noreferrer" data-testid={`et-original-${e.eticket_number}`}>
                      <Button size="sm" variant="ghost" title="PDF Asli OTA" className="h-8 text-xs">
                        <FileText size={14} className="mr-1 text-slate-500"/> Asli
                      </Button>
                    </a>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => del(e.id)} className="h-8">
                    <Trash2 size={14} className="text-red-600"/>
                  </Button>
                </div>
              </div>
            ))}
            {list.length === 0 && <div className="text-center text-slate-400 py-10">Belum ada e-ticket.</div>}
          </div>

          {/* Desktop: table view */}
          <div className="hidden md:block overflow-x-auto">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead>E-Ticket #</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Passenger</TableHead>
                <TableHead>PNR</TableHead>
                <TableHead>Airline</TableHead>
                <TableHead>Route</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="mono font-semibold">{e.eticket_number}</TableCell>
                  <TableCell>{shortDate(e.created_at)}</TableCell>
                  <TableCell>{e.passenger}</TableCell>
                  <TableCell className="mono">{e.pnr}</TableCell>
                  <TableCell>{e.airline}</TableCell>
                  <TableCell className="text-slate-500">{e.route}</TableCell>
                  <TableCell className="text-right">
                    <a href={pdfUrl(e.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" title="Gogojalan E-Ticket"><Download size={14}/></Button></a>
                    {e.original_ota_filename && (
                      <a href={originalUrl(e.id)} target="_blank" rel="noreferrer" data-testid={`et-original-${e.eticket_number}`}><Button size="sm" variant="ghost" title="PDF Asli OTA"><FileText size={14} className="text-slate-500"/></Button></a>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(e.id)}><Trash2 size={14} className="text-red-600"/></Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Belum ada e-ticket.</TableCell></TableRow>}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
